import { Card, Flex, Statistic } from 'antd'
import { useCallback, useContext, useMemo, useState } from 'react'
import { centsToDollarsDisplay } from '../../displayFormatters'
import VSpace from '../../common/VSpace'
import AllInvoiceListTable, { InvoiceListMetadata } from '../InvoiceList/AllInvoiceListTable'
import InvoiceActionModals from './InvoiceActionModals'
import InvoiceActionsContext, { ActiveModal, InvoiceActionModalType } from './InvoiceActionsContext'
import AllInvoicesFilters, { AllInvoicesFilterValues } from './AllInvoicesFilters'
import BulkActionBar from './BulkActionBar'
import BulkSendConfirmationModal from './BulkSendConfirmationModal'
import BulkDeleteConfirmationModal from './BulkDeleteConfirmationModal'
import BatchInvoicingUpgradeModal from '../BatchInvoicingUpgradeModal'
import { InvoiceSummaryWithCustomerJobData } from '../../../domain/invoicing/invoice'
import { StatementSummary } from '../../../domain/invoicing/statement'
import {
  useBulkDeleteInvoicesMutation,
  useBulkSendInvoicesMutation,
  useBulkSyncInvoicesMutation,
  useFetchAllInvoiceIdsMutation,
} from '../../../domain/invoicing/invoicingQueries'
import { useInvoicingSettingsQuery, useSynkedUPPaymentsStatus } from '../../../domain/invoicing/settings/invoicingSettingsQueries'
import { defaultInvoicingSettings } from '../../../domain/invoicing/settings/invoicingSettings'
import { BatchCreateResponse, InvoiceSendOptions } from '../../../domain/invoicing/invoicingApi'
import useFeatureFlag from '../../../hooks/useFeatureFlag'
import { AuthenticationContext } from '../../../AuthenticationContext'
import { useInvoiceSelection } from './useInvoiceSelection'
import * as allInvoices from './allInvoicesModel'
import { unknownTooltip } from '../synkedUPPaymentsStatusModel'

type Props = {
  onBulkSendSuccess: (response: BatchCreateResponse) => void
  onBulkDeleteSuccess?: (response: BatchCreateResponse) => void
  onBulkSyncSuccess?: (response: BatchCreateResponse) => void
}

const AllInvoicesView = ({ onBulkSendSuccess, onBulkDeleteSuccess, onBulkSyncSuccess }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const isTenantConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks === true
  const bulkSendEnabled = useFeatureFlag('dev-bulk-send')
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const invoicingSettings = useMemo(
    () => invoicingSettingsQuery.data ?? defaultInvoicingSettings(),
    [invoicingSettingsQuery.data]
  )
  const synkedUPPaymentsStatus = useSynkedUPPaymentsStatus()
  const { isTenantOnSynkedUPPayments, isKnown: isSynkedUPPaymentsStatusKnown } = synkedUPPaymentsStatus
  const [filters, setFilters] = useState<AllInvoicesFilterValues>({
    paidStatus: 'not-paid',
    search: '',
  })
  const isPaidView = filters.paidStatus === 'paid'
  const sendDisabledTooltip = isPaidView ? "Paid invoices can't be sent." : unknownTooltip(synkedUPPaymentsStatus)
  const deleteDisabledTooltip = isPaidView ? "Paid invoices can't be deleted." : undefined
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null)
  const [metadata, setMetadata] = useState<InvoiceListMetadata | null>(null)
  const [loadedSummaries, setLoadedSummaries] = useState<InvoiceSummaryWithCustomerJobData[]>([])
  const [loadedStatements, setLoadedStatements] = useState<StatementSummary[]>([])
  const selection = useInvoiceSelection()
  const [showBulkSendModal, setShowBulkSendModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const handleFiltersChange = (next: AllInvoicesFilterValues) => {
    setFilters(next)
    selection.clear()
  }

  const fetchIdsMutation = useFetchAllInvoiceIdsMutation()

  const bulkSendMutation = useBulkSendInvoicesMutation((response) => {
    setShowBulkSendModal(false)
    selection.clear()
    onBulkSendSuccess(response)
  })

  const bulkDeleteMutation = useBulkDeleteInvoicesMutation((response) => {
    setShowBulkDeleteModal(false)
    selection.clear()
    onBulkDeleteSuccess?.(response)
  })

  const bulkSyncMutation = useBulkSyncInvoicesMutation((response) => {
    selection.clear()
    onBulkSyncSuccess?.(response)
  })

  const count = selection.count
  const totalCents = selection.totalCents(metadata?.totalInvoicedCents ?? 0)
  const alreadySentCount = selection.alreadySentCount(metadata?.sentCount ?? 0)
  const anyExportedInSelection = selection.anyExported(loadedSummaries, loadedStatements)

  const handleSelectAll = () => {
    fetchIdsMutation.mutate(allInvoices.toIdsRequest(filters), {
      onSuccess: (response) => selection.selectAll(response)
    })
  }

  const handleBulkSend = (invoiceUpdates: InvoiceSendOptions) => {
    bulkSendMutation.mutate({ ...selection.toBulkRequest(), invoiceUpdates })
  }

  const handleBulkDelete = (deleteFromQuickBooks: boolean) => {
    bulkDeleteMutation.mutate({ ...selection.toBulkRequest(), deleteFromQuickBooks })
  }

  const handleBulkSync = () => {
    bulkSyncMutation.mutate(selection.toBulkRequest())
  }

  const openModal = useCallback(
    (type: InvoiceActionModalType, invoiceSummary: InvoiceSummaryWithCustomerJobData) => setActiveModal({ type, invoiceSummary }),
    []
  )
  const closeModal = useCallback(() => setActiveModal(null), [])
  const invoiceActionsContextValue = useMemo(
    () => ({ activeModal, openModal, closeModal }),
    [activeModal, openModal, closeModal]
  )

  return (
    <InvoiceActionsContext.Provider value={invoiceActionsContextValue}>
      <VSpace>
        <div style={{ backgroundColor: 'white', padding: 8 }}>
          <AllInvoicesFilters filters={filters} onChange={handleFiltersChange} />
        </div>

        <Flex gap={16}>
          <Card style={{ flex: 1 }}>
            <Statistic title="Total Unpaid" value={centsToDollarsDisplay(metadata?.totalUnpaid) ?? '-'} />
          </Card>
          <Card style={{ flex: 1 }}>
            <Statistic title="Total Overdue" value={centsToDollarsDisplay(metadata?.totalOverdue) ?? '-'} />
          </Card>
        </Flex>

        {bulkSendEnabled && (
          <BulkActionBar
            selectedCount={count}
            selectedTotalCents={totalCents}
            totalAvailable={metadata?.totalCount ?? 0}
            isTenantConnectedToQuickBooks={isTenantConnectedToQuickBooks}
            isSelectAllLoading={fetchIdsMutation.isLoading}
            onSelectAll={handleSelectAll}
            onClear={selection.clear}
            onDelete={() => setShowBulkDeleteModal(true)}
            onSync={handleBulkSync}
            sendDisabledTooltip={sendDisabledTooltip}
            deleteDisabledTooltip={deleteDisabledTooltip}
            onSend={() => {
              if (!isSynkedUPPaymentsStatusKnown) {
                return
              }
              if (isTenantOnSynkedUPPayments) {
                setShowBulkSendModal(true)
              } else {
                setShowUpgradeModal(true)
              }
            }}
          />
        )}

        <Card>
          <AllInvoiceListTable
            filters={filters}
            onMetadataChange={setMetadata}
            onSummariesLoaded={setLoadedSummaries}
            onStatementsLoaded={setLoadedStatements}
            isRowSelected={bulkSendEnabled ? selection.isRowSelected : undefined}
            onToggleRow={bulkSendEnabled ? selection.toggleRow : undefined}
            isStatementSelected={bulkSendEnabled ? selection.isStatementSelected : undefined}
            onToggleStatement={bulkSendEnabled ? selection.toggleStatement : undefined}
          />
        </Card>
        <InvoiceActionModals />

        <BulkSendConfirmationModal
          open={showBulkSendModal}
          onClose={() => setShowBulkSendModal(false)}
          onConfirm={handleBulkSend}
          selectedCount={count}
          selectedTotalCents={totalCents}
          alreadySentCount={alreadySentCount}
          invoicingSettings={invoicingSettings}
          isLoading={bulkSendMutation.isLoading}
        />

        <BulkDeleteConfirmationModal
          open={showBulkDeleteModal}
          onClose={() => setShowBulkDeleteModal(false)}
          onConfirm={handleBulkDelete}
          selectedCount={count}
          selectedTotalCents={totalCents}
          showDeleteFromQuickBooksOption={anyExportedInSelection}
          isLoading={bulkDeleteMutation.isLoading}
        />

        <BatchInvoicingUpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      </VSpace>
    </InvoiceActionsContext.Provider>
  )
}

export default AllInvoicesView
